/**
 * `DataTable` — every data table in the product sorts by its headers and filters by its
 * columns, through ONE component. `docs/adr/9009`.
 *
 * Planner review, 2026-09-25: "per tutte le tabelle del sistema, prevediamo la possibilità di
 * ordinarle per colonne e di filtrarle … una feature trasversale e non puntuale". Before this
 * there were two sortable tables in the product (the planning grid, the elasticity explorer's
 * article list), each with its own header, and none that filtered. A capability repeated by
 * hand in forty tables is forty capabilities that drift; this is one.
 *
 * ── THE PIPELINE, AND WHY IN THIS ORDER ────────────────────────────────────────────
 *
 *   rows ──► filter ──► sort ──► cap (fifty, ADR 9008) ──► draw
 *                          └──────────────────────────────► CSV of the view
 *
 * Filtering before sorting is only cheaper. Sorting before capping is the one that matters: a
 * cap applied first would sort the fifty rows it happened to keep, and the head of the table
 * would stop being the head of the order. The CSV takes the view — filtered and sorted — and
 * states both in its perimeter, which is G2.4's fidelity rule ("the export reflects the current
 * filters, sort, column set and drill level").
 *
 * ── WHY FILTERING IS HONEST HERE WHEN ADR 0097 REFUSED IT IN THE GRID ─────────────────
 *
 * ADR 0097's refusal had two grounds. One is that a client-side filter "runs over what happened
 * to be loaded, which for a cursor-paginated collection is an arbitrary prefix of the estate".
 * The other is that a filtered hierarchy with server-computed totals would contradict those
 * totals. The first does not hold for the tables this component draws: `useApiCollection` reads
 * EVERY page (`collectPages`, which refuses rather than truncates past `MAX_PAGES`), so the rows
 * in hand are the whole perimeter, not a prefix. A table whose read IS a prefix says so
 * (`partialRead`), and the filter line then states that it filters the rows read. The second
 * ground still holds, and it is why the planning grid is not built on this component.
 *
 * The count is always stated: "12 of 340 rows match". The screen's tiles and totals are NOT
 * filtered. They are the perimeter's figures, computed by the chapter over every row, and a
 * filter that silently changed them would be the "3 exceptions of 300" ADR 0097 warned about.
 *
 * ── AN ABSENCE IS NOT A SMALL NUMBER ─────────────────────────────────────────────────
 *
 * A column's `value` returns `null` or `undefined` where the platform published nothing. That
 * sorts LAST in BOTH directions. It is not the smallest measurement, because it is not a
 * measurement, and reversing the sort must not promote the gaps to the top of the screen. The
 * planning grid (ADR 0091) follows the same rule. For the same reason an absent value never
 * satisfies a numeric or date range; a values filter offers "no value" as an option of its own,
 * so a planner who wants the gaps can ask for them by name.
 */

import { Fragment, cloneElement, isValidElement, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter, X } from 'lucide-react';

import {
  NOT_PUBLISHED, NO_PRINCIPAL, buildVersion, isAbsent,
  type ExportCell, type ExportColumn, type ExportRequest, type ProvenanceFilter,
} from '../export';
import {
  AboutPopover, AbsenceNotes, BarCell, Button, Checkbox, GhostBar, GhostProvider, IconButton, Input, LoadingIndicator, NumericCell, Popover,
  StatusMark, TruncatedText, useReadInFlight, type StatusMarkKind,
} from '../primitives';
import { FOCUS_RING_INSET } from '../primitives/focus';
import { densityRowStyle, useDensity } from './density';
import { DENSITY_TOTAL_ROW_HEIGHT } from '../tokens/generated/tokens';
import { RowCapNotice, TABLE_ROW_CAP, type DownloadLabelling } from './row-cap';
import { RankFilterBar, type RankedCap, type RankedGrain } from './ranked-cap';
import { ConstantsBand } from './constants-band';
import { ArticleThumbnail, ArticleThumbnailProvider } from './product-image/thumbnail';
import { formatDate, formatDateTime, displayNumberFormat } from '../lib/format';

// ── the column contract ─────────────────────────────────────────────────────────

/**
 * How a column sorts and which filter it offers.
 *
 *   `text`    alphanumeric sort ("Article 10" after "Article 9"), filtered by COLLECTING values
 *   `number`  numeric sort, a from/to range
 *   `date`    ISO-8601 sort, a from/to range on the same strings
 *   `enum`    sorted by its label, filtered by picking values from the ones present
 *
 * A column with no `kind` is neither sortable nor filterable: an action column, a checkbox.
 */
export type DataColumnKind = 'text' | 'number' | 'date' | 'enum';

/** A cell's value for sorting and filtering. `null`/`undefined` is an absence, never a zero. */
export type DataValue = string | number | null | undefined;

export interface DataColumn<T> {
  /**
   * Stable within the table, and the key the view's sort and filters are stated under in the
   * export's perimeter. Use the export column's own key where the table has one.
   */
  readonly id: string;
  readonly headerKey: string;
  readonly headerValues?: Readonly<Record<string, string | number>>;
  readonly align?: 'start' | 'end';
  readonly kind?: DataColumnKind;
  /** The value the column sorts and filters on. Required when `kind` is set. */
  readonly value?: (row: T) => DataValue;
  /**
   * What the column SORTS on, where that is not what it filters on: a size filters by its name
   * and sorts by its position in the scale's run (`sizeSortValue`, ADR 9027). Compared as
   * numbers; absent last, as every absence sorts.
   */
  readonly sortValue?: (row: T) => DataValue;
  /** An enum value as the planner reads it — the same words the cell shows. */
  readonly label?: (value: string) => string;
  /** The WHOLE cell, `<td>` or `<th scope="row">` — a table's existing markup, kept verbatim. */
  readonly cell: (row: T) => React.ReactNode;
  readonly headerClassName?: string;
  /**
   * The header's drawn content, where it is more than words — a chart's series name with its
   * panel's unit beneath it. `headerKey` still names the column for the filter and the chips.
   */
  readonly header?: React.ReactNode;
  /**
   * An (i) drawn after the header, OUTSIDE its sort button — a control may not nest in another.
   * What the column's data really is: its definition, grain, source and limits (ADR 9046).
   */
  readonly headerHelp?: React.ReactNode;
  /**
   * What the column's data really is — definition, unit or sign, what an empty cell means, and
   * whether the figure is the platform's or derived here (DEC-111, ADR 9047). Drawn as an (i)
   * after the header, followed by the table's `sourceKey` paragraph; carried into the export's
   * column guide (`ExportColumn.descriptionKey`, ADR 9045), so screen and file say the same thing.
   * `headerHelp`, where given, is drawn instead. The census (`src/test/info-census.test.ts`) fails
   * a column on a registered screen with neither.
   */
  readonly descriptionKey?: string;
  /** Values for `descriptionKey`'s placeholders, over the table's `helpValues`. */
  readonly descriptionValues?: Readonly<Record<string, string | number>>;
  /**
   * `false` keeps the column sortable but offers no filter. For a table whose rows hold live
   * state a filter must not unmount — a week whose decision is being recorded writes the
   * platform's answer from inside its row (the markdown week queue). Sorting only reorders.
   */
  readonly filterable?: boolean;
  /**
   * The grey shape this column takes in a ghost table (spec v6b §2, ADR 9013). Defaults to
   * `number` for a `number` column and `text` otherwise. `bar` for a bar-cell column, `tag`
   * for a status chip, `thumbnail` for a 44px image box.
   */
  readonly ghost?: GhostShape;
  /**
   * Opt in to saying a constant ONCE (client rule 2, `docs/adr/9028`): when every drawn row
   * has the same present value in this column, the column is not drawn and its value is
   * stated once in a line above the table — "Currency: EUR" — rather than on every row. With
   * two values it is an ordinary column again. Needs `value`; the hoisted words are `label`'s
   * where the column has one, the number in the locale's format for a `number` column, the
   * value verbatim otherwise. The CSV keeps the column on every row: a file is read without
   * the line above it. Ignored with `renderRow`, whose rows draw their own cells.
   */
  readonly constantHoist?: true;
  /**
   * An absent value is legitimately EMPTY, not unreported: the field does not apply to the row (a
   * lane-book line released under no cap has no tranche week, and ships as before). The table's file
   * leaves the cell blank instead of `not-reported` / «n.d.» — which would say the platform failed to
   * state something it did state.
   */
  readonly blankWhenAbsent?: true;
  /**
   * The column's values are codes or ids — a run id, a store key — drawn in the mono face where
   * the table draws them itself: in the constants band, when the column is hoisted (brief part 1
   * §1). An `enum` column with no `label` shows its codes verbatim and counts as one without
   * saying so; a `number` column is always mono.
   */
  readonly code?: true;
  /**
   * The column that matters — audit v5b D-03, rendered per brief part 1 §1 (pkgC2). ONE per
   * table. Its cells sit on `surface-emphasis` at 600 (`text-body`, which follows the density);
   * its header on `surface-emphasis-head` under a 3px ink rule, in ink (secondary ink is 4.16:1
   * there); its cell in the totals row on `surface-emphasis-head` at the total's 700. Applied to
   * a plain `<td>`/`<th>` cell and to `NumericCell`/`BarCell`; a cell drawn by another component
   * draws its own.
   */
  readonly emphasis?: true;
}

/** The silhouette of one ghost cell. */
export type GhostShape = 'text' | 'number' | 'bar' | 'tag' | 'thumbnail';

/** The 44px product thumbnail column — see `DataTableProps.thumbnail`. */
export interface DataTableThumbnail<T> {
  /** The row's `ARV_ARTICLE`, verbatim. */
  readonly articleKey: (row: T) => string;
  /** The column's (visually hidden) header. Defaults to "Photo". */
  readonly headerKey?: string;
}

export interface DataTableProps<T> {
  readonly rows: readonly T[];
  readonly columns: readonly DataColumn<T>[];
  readonly rowKey: (row: T) => string;
  readonly captionKey?: string;
  readonly captionValues?: Readonly<Record<string, string | number>>;
  /** The caption is read to assistive technology only (`sr-only`), as most tables here do. */
  readonly captionHidden?: boolean;
  readonly rowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement>;
  /**
   * A row that is more than one `<tr>` — an expandable row, say. When given, it draws the
   * row itself and the columns supply only the header, the sort and the filter.
   */
  readonly renderRow?: (row: T) => React.ReactNode;
  /** The export of the VIEW: called with the filtered, sorted rows. Absent: no download. */
  readonly exportRequest?: (rows: readonly T[]) => ExportRequest | Promise<ExportRequest>;
  /**
   * Rows drawn before the rest go to the CSV — ADR 9008. `false` draws every row, and so does
   * a table with no `exportRequest`: rows cut with nowhere to go would be rows nobody can reach.
   */
  readonly cap?: number | false;
  /**
   * The rows are one page of a longer read, not the whole perimeter — the in-season stock
   * panel's 200. The filter line then says it filters the rows read.
   */
  readonly partialRead?: boolean;
  /** The table already states its own drawn/total count; the cap line draws the download alone. */
  readonly countStated?: boolean;
  readonly className?: string;
  readonly headerRowClassName?: string;
  /** Wrap the table in a horizontal scroller. Most tables here have one; default on. */
  readonly scroll?: boolean;
  /** `aria-describedby` for the `<table>` — a chart's table view points at the chart's caption. */
  readonly describedBy?: string;
  /**
   * The CSV download — ON for every table (ADR 9010: "su tutte le tabelle ci sia"). `false`
   * only where the surface already carries its own export of the same data, so the planner
   * is not offered two buttons for one file: a chart's table view inside a KPI tile.
   */
  readonly download?: boolean;
  /** The export's id and title when the table has no builder of its own (see `tableRequest`). */
  readonly exportId?: string;
  readonly exportTitleKey?: string;
  /**
   * A `<tfoot>` — a path's totals row. It is the chapter's figure over EVERY row, like the
   * tiles above a table, and is drawn unchanged whatever the view: a total that followed the
   * filter would be a number the platform did not compute (ADR 0097).
   */
  readonly footer?: React.ReactNode;
  /**
   * The silhouette (spec v6b §2, ADR 9013): the real headers over `ghostRows` grey rows, and
   * nothing that acts on data — no sort, no filter, no download, no count. `rows` is ignored,
   * so a screen passes the same props in every state and the table does not move when the
   * data lands. Ghost is not zero: no row count is stated, because "0 rows" would be one.
   */
  readonly ghost?: boolean;
  /** Rows in the silhouette. Default 5 — the designer's number. */
  readonly ghostRows?: number;
  /**
   * A leading 44px product-image column (audit v6 V6-09) for article rows, resolved in ONE
   * batched read for the rows DRAWN. The column has no `value`: it is not sortable, not
   * filterable and not exported, so nothing can count, sort or filter on image presence
   * (leader ruling 7). `src/planning/product-image/thumbnail.tsx` says why a table may carry
   * one when the planning grid may not.
   */
  readonly thumbnail?: DataTableThumbnail<T>;
  /**
   * A totals row, drawn through the columns' own cells in the `<tfoot>` over a 2px ink rule in the
   * semibold face — audit v5b D-03, brand guide p.57. Like `footer` it is the chapter's figure over
   * EVERY row and stays unchanged whatever the view (ADR 0097): it is not sorted, filtered,
   * capped or exported as a row. `footer`, where also given, follows it.
   */
  readonly totalRow?: T;
  /**
   * Row height and text size follow the planner's density choice — Compact 32 / Normal 40 /
   * Comfortable 48 px, text 13 / 14 / 14 (brief part 1 §1, re-stepping design/09 §6) — as
   * `PlanningGrid`'s do (audit A8). The totals row is 44px in every density. Opt-in so no
   * screen changes height unreviewed. A `rowProps` style still wins.
   */
  readonly density?: boolean;
  /**
   * The table is ranked and capped by the planner (`useRankedCap`, `docs/adr/9034`): it draws
   * `ranked.shown`, the rank-and-filter bar replaces the header's sort and filters, and the CSV
   * (`exportRequest`, or the table as drawn) takes every row of `ranked.filtered`. `rows` is
   * ignored.
   */
  readonly ranked?: RankedCap<T>;
  /**
   * The guided tour's anchor for this table (`data-tour`, ADR 9044) — set by a screen whose tour
   * has a step about it. A literal at the call site, so `tools/doc-sections.mjs` can find it.
   */
  readonly tourAnchor?: string;
  /** With `ranked`: the table carries a decision over a set, and the bar says what it takes. */
  readonly bulk?: boolean;
  /** With `ranked`: what one row is, said in the count and its (i) (bata-ui#302 A). */
  readonly rankedGrain?: RankedGrain<T>;
  /**
   * What the download buttons say the files hold, when it is not the count of rows drawn — a file that
   * leaves lines out of its table (the dropped doors', ADR 6183). The figures are read from the rows the
   * file is made of; a table whose rows are a part of the file's (`partialRead`) gives them from the platform.
   */
  readonly downloadLabelling?: DownloadLabelling<T>;
  /**
   * Where every row comes from — the read (and its run), the grain (one row per …), the as-of —
   * as a catalogue key. Shown as the last paragraph of every column's (i) (DEC-111, ADR 9047),
   * so the source is stated once per table and read wherever the planner asks.
   */
  readonly sourceKey?: string;
  /** The figures the (i) texts name, from the screen's reads — a run id, a stock date. */
  readonly helpValues?: Readonly<Record<string, string | number>>;
  /**
   * The table's name in the address bar (audit C11): its sort is `?<urlKey>.sort=<column>:asc`
   * and each filter `?<urlKey>.f.<column>=…`, restored on load and on back/forward. Defaults to a
   * key derived from the table's export id, caption and columns; give one where two tables on a
   * page would derive the same. `false` keeps the view out of the URL.
   */
  readonly urlKey?: string | false;
}

/** Rows in a ghost table — spec v6b §2: "5 righe grigie da 12px". */
export const GHOST_TABLE_ROWS = 5;

// ── the view state ──────────────────────────────────────────────────────────────

type Direction = 'asc' | 'desc';

type ColumnFilter =
  | { readonly kind: 'contains'; readonly text: string }
  | { readonly kind: 'range'; readonly from: string; readonly to: string }
  | { readonly kind: 'values'; readonly values: readonly string[] };

/** The key an absent value is picked under in a values filter. Never a real value. */
/**
 * How many values a narrowable picker lists at once. A table of seven thousand lines carries some
 * thousands of article codes, and a list nobody can reach the end of is not a control — so it
 * lists the first two hundred that MATCH what was typed and says how many matched.
 */
const VALUE_OPTION_CAP = 200;

const NO_VALUE = '\u0000none';

function isPresent(value: DataValue): value is string | number {
  return value !== null && value !== undefined && value !== '';
}

function isActive(filter: ColumnFilter | undefined): filter is ColumnFilter {
  if (filter === undefined) return false;
  if (filter.kind === 'contains') return filter.text.trim() !== '';
  if (filter.kind === 'range') return filter.from.trim() !== '' || filter.to.trim() !== '';
  return filter.values.length > 0;
}

/** A range bound as the column compares it: a number for `number`, the ISO string for `date`. */
function bound(kind: DataColumnKind, text: string): number | string | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (kind === 'number') {
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return trimmed;
}

function matches<T>(column: DataColumn<T>, filter: ColumnFilter, row: T, locale: string): boolean {
  const value = column.value?.(row);
  if (filter.kind === 'values') {
    const key = isPresent(value) ? String(value) : NO_VALUE;
    return filter.values.includes(key);
  }
  // An absence never satisfies a text or a range condition — see the module note.
  if (!isPresent(value)) return false;
  if (filter.kind === 'contains') {
    const needle = filter.text.trim().toLocaleLowerCase(locale);
    const haystack = column.kind === 'enum' && column.label
      ? `${String(value)} ${column.label(String(value))}`
      : String(value);
    return haystack.toLocaleLowerCase(locale).includes(needle);
  }
  const kind = column.kind ?? 'text';
  const from = bound(kind, filter.from);
  const to = bound(kind, filter.to);
  const comparable = kind === 'number' ? Number(value) : String(value);
  if (kind === 'number' && !Number.isFinite(comparable as number)) return false;
  if (from !== null && comparable < from) return false;
  if (to !== null && comparable > to) return false;
  return true;
}

/** The view as the export's perimeter states it. */
function viewFilters<T>(
  columns: readonly DataColumn<T>[], filters: Readonly<Record<string, ColumnFilter>>,
): readonly ProvenanceFilter[] {
  const out: ProvenanceFilter[] = [];
  for (const column of columns) {
    const filter = filters[column.id];
    if (!isActive(filter)) continue;
    if (filter.kind === 'contains') out.push({ field: column.id, operator: 'contains', value: filter.text.trim() });
    else if (filter.kind === 'values') {
      out.push({
        field: column.id,
        operator: 'in',
        value: filter.values.map((value) => (value === NO_VALUE ? '(no value)' : value)).join(', '),
      });
    } else {
      if (filter.from.trim() !== '') out.push({ field: column.id, operator: 'gte', value: filter.from.trim() });
      if (filter.to.trim() !== '') out.push({ field: column.id, operator: 'lte', value: filter.to.trim() });
    }
  }
  return out;
}

/**
 * The table's own export request, restated for the view: the view's filters are ADDED to the
 * perimeter the builder stated (the read's own bounds stay), and the view's sort REPLACES the
 * builder's, because the rows now leave in the view's order. A provenance filter list declared
 * absent (ADR 9007) stays absent rather than being replaced by the view's few rows of it.
 */
export function withView(
  request: ExportRequest,
  filters: readonly ProvenanceFilter[],
  sort: { readonly key: string; readonly direction: Direction } | null,
): ExportRequest {
  if (filters.length === 0 && sort === null) return request;
  return {
    ...request,
    provenance: isAbsent(request.provenance.filters)
      ? request.provenance
      : { ...request.provenance, filters: [...request.provenance.filters, ...filters] },
    perimeter: {
      ...request.perimeter,
      filters: [...request.perimeter.filters, ...filters],
      sort: sort === null ? request.perimeter.sort : [sort],
    },
  };
}

/** Filter, then sort — the view, before the cap. Exported for the tests. */
export function viewRows<T>(
  rows: readonly T[],
  columns: readonly DataColumn<T>[],
  filters: Readonly<Record<string, ColumnFilter>>,
  sort: { readonly id: string; readonly direction: Direction } | null,
  locale: string,
): readonly T[] {
  const active = columns.filter((column) => isActive(filters[column.id]));
  const filtered = active.length === 0
    ? rows
    : rows.filter((row) => active.every((column) => matches(column, filters[column.id]!, row, locale)));
  const column = sort === null ? undefined : columns.find((c) => c.id === sort.id);
  if (sort === null || column?.value === undefined) return filtered;
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
  const read = column.sortValue ?? column.value;
  const byNumber = column.kind === 'number' || column.sortValue !== undefined;
  const keyOf = (row: T): DataValue => {
    const value = read(row);
    if (column.sortValue !== undefined) return value;
    return column.kind === 'enum' && column.label && isPresent(value) ? column.label(String(value)) : value;
  };
  const sign = sort.direction === 'asc' ? 1 : -1;
  // Decorate with the original index so equal keys keep the chapter's own order.
  return filtered
    .map((row, index) => ({ row, index, key: keyOf(row) }))
    .sort((a, b) => {
      const aPresent = isPresent(a.key);
      const bPresent = isPresent(b.key);
      // Absent LAST in both directions — never flipped by `sign`.
      if (!aPresent || !bPresent) return aPresent === bPresent ? a.index - b.index : aPresent ? -1 : 1;
      if (byNumber) {
        // A number column can hold a present value that is not a number — a registry
        // parameter recorded as text. It is not an absence, so it does not go with the gaps;
        // it sorts after the numbers, among its own kind, in both directions.
        const aNumber = Number(a.key);
        const bNumber = Number(b.key);
        const aNumeric = Number.isFinite(aNumber);
        const bNumeric = Number.isFinite(bNumber);
        if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
        const order = aNumeric ? aNumber - bNumber : collator.compare(String(a.key), String(b.key));
        return order === 0 ? a.index - b.index : sign * order;
      }
      const order = collator.compare(String(a.key), String(b.key));
      return order === 0 ? a.index - b.index : sign * order;
    })
    .map((entry) => entry.row);
}

export type { ColumnFilter as DataTableFilter };

// ── the view in the address bar (audit C11) ─────────────────────────────────────

type SortState = { readonly id: string; readonly direction: Direction } | null;

/** A table's view as the URL holds it. */
export interface TableUrlView {
  readonly sort: SortState;
  readonly filters: Readonly<Record<string, ColumnFilter>>;
}

/**
 * A short, stable name for a table in the address bar, from what identifies it — its export id
 * or caption and its column ids. FNV-1a, base 36: `t` and six characters. Exported for the tests.
 */
export function tableUrlKey(seed: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `t${hash.toString(36).padStart(6, '0').slice(-6)}`;
}

const sortParam = (key: string): string => `${key}.sort`;
const filterParamPrefix = (key: string): string => `${key}.f.`;

/** One filter as its URL value: `c:<text>`, `r:<from>~<to>`, `v:<value>,<value>` (each escaped). */
function encodeFilter(filter: ColumnFilter): string {
  if (filter.kind === 'contains') return `c:${filter.text}`;
  if (filter.kind === 'range') return `r:${filter.from}~${filter.to}`;
  return `v:${filter.values.map((value) => encodeURIComponent(value)).join(',')}`;
}

function decodeFilter(raw: string): ColumnFilter | null {
  const body = raw.slice(2);
  if (raw.startsWith('c:')) return { kind: 'contains', text: body };
  if (raw.startsWith('r:')) {
    const at = body.indexOf('~');
    if (at < 0) return null;
    return { kind: 'range', from: body.slice(0, at), to: body.slice(at + 1) };
  }
  if (raw.startsWith('v:')) {
    try {
      const values = body === '' ? [] : body.split(',').map((value) => decodeURIComponent(value));
      return { kind: 'values', values };
    } catch {
      return null; // a hand-edited link with a broken escape is no filter, not an error
    }
  }
  return null;
}

/**
 * The sort and filters `search` names for the table `key`, kept only for columns the table has
 * and only where the column offers them. Pure — exported for the tests.
 */
export function readTableUrlView<T>(search: string, key: string, columns: readonly DataColumn<T>[]): TableUrlView {
  const params = new URLSearchParams(search);
  const byId = new Map(columns.map((column) => [column.id, column]));
  let sort: SortState = null;
  const rawSort = params.get(sortParam(key));
  if (rawSort !== null) {
    const at = rawSort.lastIndexOf(':');
    const id = at > 0 ? rawSort.slice(0, at) : '';
    const direction = rawSort.slice(at + 1);
    const column = byId.get(id);
    if (column?.kind !== undefined && column.value !== undefined && (direction === 'asc' || direction === 'desc')) {
      sort = { id, direction };
    }
  }
  const filters: Record<string, ColumnFilter> = {};
  const prefix = filterParamPrefix(key);
  for (const [name, raw] of params) {
    if (!name.startsWith(prefix)) continue;
    const column = byId.get(name.slice(prefix.length));
    if (column?.kind === undefined || column.value === undefined || column.filterable === false) continue;
    const filter = decodeFilter(raw);
    if (filter !== null && isActive(filter)) filters[column.id] = filter;
  }
  return { sort, filters };
}

/** `search` with the table `key`'s view written and every other key kept. Pure — exported for the tests. */
export function writeTableUrlView(search: string, key: string, view: TableUrlView): string {
  const params = new URLSearchParams(search);
  const prefix = filterParamPrefix(key);
  for (const name of [...params.keys()]) if (name.startsWith(prefix)) params.delete(name);
  if (view.sort === null) params.delete(sortParam(key));
  else params.set(sortParam(key), `${view.sort.id}:${view.sort.direction}`);
  for (const [id, filter] of Object.entries(view.filters)) {
    if (isActive(filter)) params.set(`${prefix}${id}`, encodeFilter(filter));
  }
  const next = params.toString();
  return next === '' ? '' : `?${next}`;
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/** Every table name this module has written to the address — for `forgetTableUrlViews`. */
const writtenUrlKeys = new Set<string>();

/**
 * Remove from the address every sort and filter a table wrote, and nothing else. Test hygiene
 * (`src/test/setup.ts`): jsdom's address outlives a test, a table's view must not.
 */
export function forgetTableUrlViews(): void {
  if (typeof window === 'undefined' || writtenUrlKeys.size === 0) return;
  let search = window.location.search;
  for (const key of writtenUrlKeys) search = writeTableUrlView(search, key, { sort: null, filters: {} });
  writtenUrlKeys.clear();
  if (search !== window.location.search) {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
  }
}

function sameView(a: TableUrlView, b: TableUrlView): boolean {
  return writeTableUrlView('', 'x', a) === writeTableUrlView('', 'x', b);
}

// ── the view state, as a hook ────────────────────────────────────────────────────

export interface TableView<T> {
  /** The filtered, sorted rows — every one, before any cap. */
  readonly view: readonly T[];
  readonly rows: readonly T[];
  readonly columns: readonly DataColumn<T>[];
  readonly sort: { readonly id: string; readonly direction: Direction } | null;
  readonly filters: Readonly<Record<string, ColumnFilter>>;
  readonly filtering: boolean;
  readonly cycleSort: (id: string) => void;
  readonly setFilter: (id: string, filter: ColumnFilter | undefined) => void;
  readonly clearFilters: () => void;
  /** The chapter's export request, restated for this view (see `withView`). */
  readonly exportView: (request: ExportRequest) => ExportRequest;
}

/**
 * The sort and filter state of one table, and the view it produces.
 *
 * Exported for the tables that draw their own body — the windowed allocation list, the
 * exception queue with its selection — so they sort and filter by the SAME rules as every
 * other table rather than by a second copy of them. Everything else uses `DataTable`.
 */
export function useTableView<T>(
  rows: readonly T[],
  columns: readonly DataColumn<T>[],
  options: {
    /**
     * The table's name in the address bar (audit C11): its sort and filters are written there with
     * `replaceState` — re-sorting is not a page the back button steps through — and read back on
     * load and on back/forward. Absent: the view lives in the component only.
     */
    readonly urlKey?: string | undefined;
  } = {},
): TableView<T> {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { urlKey } = options;
  const [initial] = useState<TableUrlView>(() => (
    urlKey === undefined ? { sort: null, filters: {} } : readTableUrlView(currentSearch(), urlKey, columns)
  ));
  const [filters, setFilters] = useState<Readonly<Record<string, ColumnFilter>>>(initial.filters);
  const [sort, setSort] = useState<SortState>(initial.sort);

  // The view → the address bar, whenever it changes; nothing is written when it already says so.
  useEffect(() => {
    if (urlKey === undefined || typeof window === 'undefined') return;
    const search = writeTableUrlView(window.location.search, urlKey, { sort, filters });
    if (search === window.location.search) return;
    writtenUrlKeys.add(urlKey);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
  }, [urlKey, sort, filters]);

  // Back and forward, and an in-app link landing with this table's keys: the address is the state.
  useEffect(() => {
    if (urlKey === undefined || typeof window === 'undefined') return undefined;
    const reread = (): void => {
      const next = readTableUrlView(window.location.search, urlKey, columns);
      setSort((current) => (sameView({ sort: current, filters: {} }, { sort: next.sort, filters: {} }) ? current : next.sort));
      setFilters((current) => (sameView({ sort: null, filters: current }, { sort: null, filters: next.filters }) ? current : next.filters));
    };
    window.addEventListener('popstate', reread);
    return () => window.removeEventListener('popstate', reread);
  }, [urlKey, columns]);

  const view = useMemo(
    () => viewRows(rows, columns, filters, sort, locale),
    [rows, columns, filters, sort, locale],
  );

  return {
    view,
    rows,
    columns,
    sort,
    filters,
    filtering: columns.some((column) => isActive(filters[column.id])),
    cycleSort: (id) => setSort((current) => {
      if (current?.id !== id) return { id, direction: 'asc' };
      if (current.direction === 'asc') return { id, direction: 'desc' };
      return null; // back to the chapter's own order
    }),
    setFilter: (id, filter) => setFilters((current) => {
      const next = { ...current };
      if (filter === undefined || !isActive(filter)) delete next[id];
      else next[id] = filter;
      return next;
    }),
    clearFilters: () => setFilters({}),
    exportView: (request) => withView(
      request,
      viewFilters(columns, filters),
      sort === null ? null : { key: sort.id, direction: sort.direction },
    ),
  };
}

// ── the header row ──────────────────────────────────────────────────────────────

export function DataTableHead<T>({
  state, rowClassName, cellClassName, className, hidden, fixed = false, sourceKey, helpValues,
}: {
  readonly state: TableView<T>;
  /** The table's source paragraph, last in every column's (i) — `DataTableProps.sourceKey`. */
  readonly sourceKey?: string | undefined;
  /** The figures the (i) texts name — `DataTableProps.helpValues`. */
  readonly helpValues?: Readonly<Record<string, string | number>> | undefined;
  /** The order and the filters are the rank-and-filter bar's (`ranked`): the header is not a control. */
  readonly fixed?: boolean;
  /** Columns not drawn — a constant said once above the table (`constantHoist`). */
  readonly hidden?: ReadonlySet<string> | undefined;
  readonly rowClassName?: string | undefined;
  /** Classes every header cell carries (padding, the sticky-header plane), before its own. */
  readonly cellClassName?: string | undefined;
  readonly className?: string | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { columns, sort, filters, rows } = state;
  return (
    <thead className={className}>
      <tr className={rowClassName}>
        {columns.filter((column) => hidden?.has(column.id) !== true).map((column) => {
          const sortable = !fixed && column.kind !== undefined && column.value !== undefined;
          const direction = sort?.id === column.id ? sort.direction : null;
          const end = column.align === 'end';
          return (
            <th
              key={column.id}
              scope="col"
              aria-sort={!sortable ? undefined : direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
              // The column is named by its header alone: the (i) inside it is a control of its own,
              // and «When (UTC) About When (UTC)» is not a column's name (DEC-111, ADR 9047).
              aria-label={column.headerHelp === undefined && column.descriptionKey !== undefined && column.header === undefined
                ? t(column.headerKey, column.headerValues ?? {})
                : undefined}
              className={[
                HEAD_CELL, cellClassName ?? '', column.headerClassName ?? '', end ? 'text-end' : 'text-start',
                column.emphasis === true ? EMPHASIS_HEAD : '',
              ].join(' ')}
              {...(column.emphasis === true ? { 'data-emphasis': '' } : {})}
            >
              {sortable ? (
                // Label, sort mark, then the filter — in that order in every column, so a
                // filter icon always sits against its own label and never reads as the
                // neighbouring column's. An end-aligned header moves the whole group.
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => state.cycleSort(column.id)}
                    className={`inline-flex items-center gap-1 text-[inherit] ${FOCUS_RING_INSET}`}
                  >
                    <span>{column.header ?? t(column.headerKey, column.headerValues ?? {})}</span>
                    {/* `data-table-control`: the sort mark is a control, not the header's words —
                        the «Documento» page model does not print it (ADR 9053, owner ruling 2026-10-09). */}
                    <span data-table-control="sort" className="inline-flex shrink-0">
                      {direction === 'asc' ? <ArrowUp size={12} aria-hidden className="shrink-0" />
                        : direction === 'desc' ? <ArrowDown size={12} aria-hidden className="shrink-0" />
                          : <ArrowUpDown size={12} aria-hidden className="text-ink-muted shrink-0" />}
                    </span>
                    <span className="sr-only">{t('table.sort.hint')}</span>
                  </button>
                  {column.headerHelp ?? <ColumnAbout column={column} sourceKey={sourceKey} helpValues={helpValues} />}
                  {column.filterable !== false && (
                    <span data-table-control="filter" className="inline-flex">
                      <FilterControl
                        column={column}
                        rows={rows}
                        filter={filters[column.id]}
                        onChange={(filter) => state.setFilter(column.id, filter)}
                      />
                    </span>
                  )}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <span>{column.header ?? t(column.headerKey, column.headerValues ?? {})}</span>
                  {column.headerHelp ?? <ColumnAbout column={column} sourceKey={sourceKey} helpValues={helpValues} />}
                </span>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

/**
 * A column's (i) — its `descriptionKey`, then the table's `sourceKey` (DEC-111, ADR 9047). Named
 * «Informazioni su <header>»; outside the sort button, since a control may not nest in another.
 */
function ColumnAbout<T>({ column, sourceKey, helpValues }: {
  readonly column: DataColumn<T>;
  readonly sourceKey: string | undefined;
  readonly helpValues: Readonly<Record<string, string | number>> | undefined;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (column.descriptionKey === undefined) return null;
  const keys = sourceKey === undefined ? [column.descriptionKey] : [column.descriptionKey, sourceKey];
  const values = { ...(helpValues ?? {}), ...(column.descriptionValues ?? {}) };
  return (
    <span data-help={column.id} className="inline-flex normal-case tracking-normal">
      <AboutPopover aboutKeys={keys} title={t(column.headerKey, column.headerValues ?? {})} values={values} size="sm" />
    </span>
  );
}

// ── the filter line ─────────────────────────────────────────────────────────────

/** How many rows match, each active filter as a removable chip, and "clear all". Nothing when unfiltered. */
export function DataTableFilterBar<T>({ state, partialRead = false }: {
  readonly state: TableView<T>;
  readonly partialRead?: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!state.filtering) return null;
  const active = state.columns.filter((column) => isActive(state.filters[column.id]));
  return (
    // `data-table-filter-bar`: the count of matching rows and each filter's words print (a printed
    // table that is filtered says so); its remove and clear buttons are controls and do not (ADR 9053).
    <div className="flex flex-wrap items-center gap-2" data-table-filter-bar="">
      <p className="text-caption text-ink-secondary" role="status">
        {t(partialRead ? 'table.filter.matchingRead' : 'table.filter.matching', {
          matching: state.view.length, total: state.rows.length,
        })}
      </p>
      {active.map((column) => (
        <span
          key={column.id}
          className="bg-surface-sunken border-hairline text-label text-ink-primary inline-flex items-center gap-1 rounded-chip border px-2 py-1"
        >
          {describe(column, state.filters[column.id]!, t)}
          <span data-table-control="filter-remove" className="inline-flex">
            <IconButton
              variant="ghost"
              size="sm"
              icon={<X size={10} aria-hidden />}
              aria-label={t('table.filter.remove', { column: t(column.headerKey, column.headerValues ?? {}) })}
              onClick={() => state.setFilter(column.id, undefined)}
              className="size-4 p-0"
            />
          </span>
        </span>
      ))}
      <span data-table-control="filter-clear" className="inline-flex">
        <Button variant="ghost" size="sm" onClick={state.clearFilters}>
          {t('table.filter.clearAll')}
        </Button>
      </span>
    </div>
  );
}

// ── the component ───────────────────────────────────────────────────────────────

export function DataTable<T>(props: DataTableProps<T>): React.JSX.Element {
  // Two components rather than one with a branch, so each keeps its hooks unconditional.
  if (props.ghost === true) return <GhostTable {...props} />;
  return <LiveTable {...props} />;
}

function LiveTable<T>({
  rows, columns: ownColumns, rowKey, captionKey, captionValues, captionHidden = true, rowProps, renderRow, totalRow, density = false,
  exportRequest, cap = TABLE_ROW_CAP, partialRead = false, countStated = false, footer, className,
  headerRowClassName, scroll = true, describedBy, download = true, exportId, exportTitleKey, thumbnail, ranked, bulk = false,
  tourAnchor, sourceKey, helpValues, urlKey, rankedGrain, downloadLabelling,
}: DataTableProps<T>): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { mode: densityMode } = useDensity();
  const columns = useMemo(
    () => (thumbnail === undefined ? ownColumns : [thumbnailColumn(thumbnail), ...ownColumns]),
    [ownColumns, thumbnail],
  );
  // A ranked table keeps its own scope in the URL (`useRankedCap`); every other names itself there.
  const resolvedUrlKey = ranked !== undefined || urlKey === false
    ? undefined
    : urlKey ?? tableUrlKey(`${exportId ?? captionKey ?? ''}|${ownColumns.map((column) => column.id).join(',')}`);
  const state = useTableView(ranked?.shown ?? rows, columns, { urlKey: resolvedUrlKey });
  const { view } = state;
  /**
   * Where the CSV comes from: the chapter's own builder when it has one (its provenance, its
   * raw columns), otherwise the table itself as drawn (`tableRequest`). Either way the rows
   * are the view's.
   */
  const request = exportRequest ?? (download
    ? (viewed: readonly T[]) => tableRequest({
      id: exportId ?? captionKey ?? 'table',
      titleKey: exportTitleKey ?? captionKey ?? columns[0]?.headerKey ?? 'table',
      columns,
      rows: viewed,
      t,
    })
    : undefined);
  // The cap needs somewhere for the rest to go. With no download a cut would hide rows the
  // planner has no way to reach, so such a table draws every row.
  const limit = request === undefined || ranked !== undefined ? false : cap;
  const shown = limit === false || view.length <= limit ? view : view.slice(0, limit);
  // The thumbnails of the rows DRAWN — one batched read, keyed by what is on screen.
  const drawnArticles = useMemo(
    () => (thumbnail === undefined ? [] : shown.map((row) => thumbnail.articleKey(row))),
    [shown, thumbnail],
  );
  // The columns that hold one value on every drawn row and asked to say it once.
  const constants = useMemo(
    () => (renderRow === undefined ? constantColumns(columns, shown, i18n.language) : []),
    [columns, shown, renderRow, i18n.language],
  );
  const hidden = useMemo(() => new Set(constants.map(({ column }) => column.id)), [constants]);
  const drawn = constants.length === 0 ? columns : columns.filter((column) => !hidden.has(column.id));
  const constantsId = useId();
  const describedByIds = [describedBy, constants.length === 0 ? undefined : constantsId]
    .filter((id): id is string => id !== undefined).join(' ');

  const table = (
    <table className={className ?? 'w-full text-sm'} aria-describedby={describedByIds === '' ? undefined : describedByIds}>
      {captionKey !== undefined && (
        <caption className={captionHidden ? 'sr-only' : 'text-start text-caption text-ink-secondary'}>
          {t(captionKey, captionValues ?? {})}
        </caption>
      )}
      <DataTableHead
        state={state}
        rowClassName={headerRowClassName}
        hidden={hidden}
        fixed={ranked !== undefined}
        sourceKey={sourceKey}
        helpValues={helpValues}
      />
      <tbody>
        {shown.length === 0 && (state.filtering || ranked?.filtering === true) ? (
          <tr>
            <td colSpan={drawn.length} className="text-body text-ink-secondary py-3">
              {t('table.filter.noMatch')}
            </td>
          </tr>
        ) : shown.map((row) => (
          renderRow !== undefined
            ? <Fragment key={rowKey(row)}>{renderRow(row)}</Fragment>
            : (
              <tr key={rowKey(row)} {...densityRow(density ? densityRowStyle(densityMode) : null, rowProps?.(row))}>
                {drawn.map((column) => <Fragment key={column.id}>{drawCell(column, row, 'body')}</Fragment>)}
              </tr>
            )
        ))}
      </tbody>
      {(totalRow !== undefined || footer !== undefined) && (
        <tfoot>
          {totalRow !== undefined && (
            <tr
              data-row-kind="total"
              className="border-ink-primary border-t-2 font-bold"
              // Brief part 1 §1: the totals row is a fixed 44px in every density — and, being
              // opt-in itself, whether or not the table follows the density — with the
              // density's text size where it does.
              style={density ? densityRowStyle(densityMode, DENSITY_TOTAL_ROW_HEIGHT) : { height: DENSITY_TOTAL_ROW_HEIGHT }}
            >
              {drawn.map((column, index) => (
                <Fragment key={column.id}>{drawCell(column, totalRow, index === 0 ? 'total-label' : 'total')}</Fragment>
              ))}
            </tr>
          )}
          {footer}
        </tfoot>
      )}
    </table>
  );

  // The constants band, directly ABOVE the header, carrying the table's 2px ink top rule —
  // the one band of the codebase (`constants-band.tsx`, brief part 1 §1, pkgC2).
  const band = (
    <ConstantsBand
      id={constantsId}
      edge="table"
      pairs={constants.map(({ column, words }) => ({
        id: column.id,
        labelKey: column.headerKey,
        ...(column.headerValues === undefined ? {} : { labelValues: column.headerValues }),
        value: words,
        mono: isCodeColumn(column),
      }))}
    />
  );
  // Every absent cell's reason is stated once, under the table (`AbsenceNotes`, ADR 9028).
  const body = (
    <AbsenceNotes>
      {band}
      {scroll ? <div className="overflow-x-auto">{table}</div> : table}
    </AbsenceNotes>
  );
  return (
    // A block of the page grid (brief part 3 §B6, ADR 9038).
    <div className="flex min-w-0 flex-col gap-2" data-layout-block="" data-tour={tourAnchor}>
      {ranked !== undefined && (
        <RankFilterBar
          ranked={ranked}
          bulk={bulk}
          {...(request === undefined ? {} : { exportRequest: request })}
          {...(rankedGrain === undefined ? {} : { grain: rankedGrain })}
          {...(downloadLabelling === undefined ? {} : { download: downloadLabelling })}
        />
      )}
      {ranked === undefined && <DataTableFilterBar state={state} partialRead={partialRead} />}
      {/* The download sits above the table, at the inline end (F7) — a toolbar, reached
          before the rows rather than after the fiftieth. */}
      {request !== undefined && ranked === undefined && (
        <RowCapNotice
          shown={shown.length}
          total={view.length}
          countStated={countStated}
          always
          exportRequest={async () => state.exportView(await request(view))}
          {...(downloadLabelling === undefined ? {} : { labels: { keys: downloadLabelling.keys, values: downloadLabelling.values(view) } })}
        />
      )}
      {thumbnail === undefined
        ? body
        : <ArticleThumbnailProvider articleKeys={drawnArticles}>{body}</ArticleThumbnailProvider>}
    </div>
  );
}

// ── a cell, drawn to the brief (one line, emphasis, totals) ─────────────────────

/** The emphasised column's cell (brief part 1 §1): the tint, the figures at 600. */
const EMPHASIS = 'text-body bg-surface-emphasis font-semibold';
/** …in the totals row: the header's step, at the total's 700. */
const EMPHASIS_TOTAL = 'text-body bg-surface-emphasis-head font-bold';
/** …and its header: the deeper tint under a 3px ink rule, the label in ink (AA on the tint). */
const EMPHASIS_HEAD = 'bg-surface-emphasis-head text-ink-primary shadow-[inset_0_3px_0_var(--bata-color-ink-primary)]';
/** The totals row's label — «Totale · 12 negozi»: 11px 700 capitals. */
const TOTAL_LABEL = 'text-micro font-bold uppercase';
/**
 * Every header cell (brief part 1 §1, the part 2 HTML's tables): 11px 700 capitals in ink, with a
 * 1px ink rule under the header. On the CELL, so a chapter's `headerRowClassName` — which colours
 * and sizes the row — cannot override it: a cell's own font and colour beat what it inherits, and
 * in the collapsed border model a cell's border beats its row's of the same width.
 */
const HEAD_CELL = 'text-micro text-ink-primary border-ink-primary border-b font-bold uppercase tracking-[0.06em]';

/** Where a cell is drawn: a body row, the totals row, or the totals row's label cell. */
type CellRole = 'body' | 'total' | 'total-label';

/** A column whose words are codes or ids — drawn mono by the table (see `DataColumn.code`). */
function isCodeColumn<T>(column: DataColumn<T>): boolean {
  return column.code === true || column.kind === 'number' || (column.kind === 'enum' && column.label === undefined);
}

/**
 * The column's cell, drawn to the brief. On a plain `<td>`/`<th>`: `emphasis` appended to its
 * classes; the totals row's label styled; and a body cell holding nothing but a string drawn
 * as `TruncatedText` — one line, an ellipsis where the column is narrower, the whole text in a
 * tooltip that opens on focus. On a `NumericCell`/`BarCell`/`StatusCell`: `emphasis` passed as
 * the prop — the cell draws its own `<td>`, so the table cannot reach its classes.
 * Anything else is drawn as the column drew it, because the table cannot know where its
 * classes belong.
 */
function drawCell<T>(column: DataColumn<T>, row: T, role: CellRole): React.ReactNode {
  const node = column.cell(row);
  if (!isValidElement(node)) return node;
  const emphasis = column.emphasis === true;
  if (node.type === 'td' || node.type === 'th') {
    const props = node.props as { className?: string; children?: React.ReactNode };
    const added = [
      emphasis ? (role === 'body' ? EMPHASIS : EMPHASIS_TOTAL) : '',
      role === 'total-label' ? TOTAL_LABEL : '',
    ].filter((name) => name !== '').join(' ');
    const truncate = role === 'body' && typeof props.children === 'string' && props.children !== '';
    if (added === '' && !truncate) return node;
    return cloneElement(
      node as React.ReactElement<{ className?: string }>,
      added === '' ? {} : { className: props.className === undefined ? added : `${props.className} ${added}` },
      ...(truncate ? [<TruncatedText key="text" text={props.children as string} />] : []),
    );
  }
  if (emphasis && (node.type === NumericCell || node.type === BarCell || node.type === StatusCell)) {
    return cloneElement(node as React.ReactElement<{ emphasis?: boolean | 'total' }>, { emphasis: role === 'body' ? true : 'total' });
  }
  return node;
}

/** A row's props with the density's height and text, under whatever style the chapter's `rowProps` set. */
function densityRow(
  style: React.CSSProperties | null, props: React.HTMLAttributes<HTMLTableRowElement> | undefined,
): React.HTMLAttributes<HTMLTableRowElement> {
  if (style === null) return props ?? {};
  return { ...(props ?? {}), style: { ...style, ...(props?.style ?? {}) } };
}

// ── a status column, compact ────────────────────────────────────────────────────

/** One row's status: the mark (symbol and colour) and the words. */
export interface RowStatus {
  /** The platform's code — what the column sorts, filters and exports on. */
  readonly code: string;
  /** ● open · ✓ good · ✕ critical · ◐ modified · — neutral (brief part 1 §1, `StatusMark`). */
  readonly mark: StatusMarkKind;
  readonly labelKey: string;
  readonly values?: Readonly<Record<string, string | number>>;
}

/**
 * A status as a table cell — audit v5b D-03, rendered per brief part 1 §1 (pkgC2): a mono
 * symbol and the word on one line at 600 (`StatusMark`), never the colour alone. A row with no
 * status says «No status» in words (ADR 9061).
 *
 * `emphasis` is set by the table when the column is its emphasised one (`DataColumn.emphasis`),
 * as it is on a `NumericCell`: the same tint and weight as every other emphasised cell, the
 * header's step in the totals row, and the words in the label grey (secondary ink is under AA
 * on the tint).
 */
export function StatusCell({ status, emphasis = false }: {
  readonly status: RowStatus | null;
  readonly emphasis?: boolean | 'total';
}): React.JSX.Element {
  const { t } = useTranslation();
  const tint = emphasis === 'total' ? ` ${EMPHASIS_TOTAL}` : emphasis ? ` ${EMPHASIS}` : '';
  return (
    <td className={`whitespace-nowrap${tint}`} data-status={status?.mark ?? ''}>
      {status === null
        ? <span className={emphasis === false ? 'text-ink-secondary' : 'text-ink-label'}>{t('table.status.none')}</span>
        : <StatusMark mark={status.mark} labelKey={status.labelKey} {...(status.values === undefined ? {} : { values: status.values })} />}
    </td>
  );
}

/**
 * A compact status column — an `enum` on the status code, drawn as `StatusCell`, ghosted as a tag.
 * `label` is the words the filter offers for a code: the same words the cell shows.
 */
export function statusColumn<T>({ id, headerKey, status, label, emphasis, descriptionKey }: {
  readonly id: string;
  readonly headerKey: string;
  /** What the status means and where it comes from — the column's (i) (DEC-111, ADR 9047). */
  readonly descriptionKey?: string;
  readonly status: (row: T) => RowStatus | null;
  readonly label: (code: string) => string;
  /** The table's emphasised column (brief part 1 §1) — drawn on the tint like any other. */
  readonly emphasis?: true;
}): DataColumn<T> {
  return {
    id,
    headerKey,
    kind: 'enum',
    ghost: 'tag',
    ...(emphasis === true ? { emphasis } : {}),
    ...(descriptionKey === undefined ? {} : { descriptionKey }),
    value: (row) => status(row)?.code ?? null,
    label,
    cell: (row) => <StatusCell status={status(row)} />,
  };
}

// ── a constant said once ────────────────────────────────────────────────────────

/**
 * The `constantHoist` columns whose drawn rows all carry the same present value, with that
 * value in words. One row is not a constant (every column of one row is "the same"), so it
 * takes two; an absent value is not a constant either — the absence is stated by its cell.
 * Exported for the tests.
 */
/**
 * A date or an instant in the planner's format, never the ISO string (usability audit F1): a
 * calendar date as a date, an instant as a date and a time in UTC (the platform's clock).
 * Unreadable: verbatim.
 */
function dateWords(value: string, locale: string | undefined): string {
  if (Number.isNaN(new Date(value).getTime())) return value;
  const language = locale ?? 'en-GB';
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value, language, 'UTC') : formatDateTime(value, language, 'UTC');
}

export function constantColumns<T>(
  columns: readonly DataColumn<T>[], rows: readonly T[], locale?: string,
): readonly { readonly column: DataColumn<T>; readonly words: string }[] {
  if (rows.length < 2) return [];
  const out: { column: DataColumn<T>; words: string }[] = [];
  for (const column of columns) {
    if (column.constantHoist !== true || column.value === undefined) continue;
    const first = column.value(rows[0]!);
    if (!isPresent(first)) continue;
    if (!rows.every((row) => column.value!(row) === first)) continue;
    const words = column.label !== undefined
      ? column.label(String(first))
      : column.kind === 'number' && typeof first === 'number'
        ? displayNumberFormat(locale ?? 'en-GB').format(first)
        : column.kind === 'date' ? dateWords(String(first), locale) : String(first);
    out.push({ column, words });
  }
  return out;
}

// ── the thumbnail column ────────────────────────────────────────────────────────

/**
 * The leading image column. No `kind` and no `value`, so `DataTableHead` offers no sort and no
 * filter on it and `tableRequest` leaves it out of the CSV — ruling 7, structurally.
 */
function thumbnailColumn<T>(thumbnail: DataTableThumbnail<T>): DataColumn<T> {
  const headerKey = thumbnail.headerKey ?? 'table.thumbnail.header';
  return {
    id: 'thumbnail',
    headerKey,
    // Named for assistive technology, silent on screen: the images need no heading to be read.
    header: <SrOnlyHeader headerKey={headerKey} />,
    ghost: 'thumbnail',
    cell: (row) => (
      <td className="py-1 pe-2">
        <ArticleThumbnail articleKey={thumbnail.articleKey(row)} />
      </td>
    ),
  };
}

function SrOnlyHeader({ headerKey }: { readonly headerKey: string }): React.JSX.Element {
  const { t } = useTranslation();
  return <span className="sr-only">{t(headerKey)}</span>;
}

// ── the silhouette ──────────────────────────────────────────────────────────────

const GHOST_WIDTH: Readonly<Record<Exclude<GhostShape, 'thumbnail'>, string>> = {
  text: '70%',
  number: '3rem',
  bar: '100%',
  tag: '3rem',
};

/**
 * One ghost cell — a `<td>` holding the column's grey shape. Exported for a table that is
 * populated but has a COLUMN still waiting (Exit plans' "Awaiting position": the plans are
 * there, the measured fields are all `null`), where the whole table is not a ghost.
 */
export function GhostCell({ shape = 'text', align }: {
  readonly shape?: GhostShape;
  readonly align?: 'start' | 'end';
}): React.JSX.Element {
  const { t } = useTranslation();
  if (shape === 'thumbnail') {
    return (
      <td className="py-1 pe-2">
        <ArticleThumbnail articleKey="" ghost />
      </td>
    );
  }
  const end = align === 'end' || shape === 'number';
  return (
    <td className="py-3" data-ghost-cell={shape}>
      <span className="sr-only">{t('ghost.value')}</span>
      <GhostBar width={GHOST_WIDTH[shape]} align={end ? 'end' : 'start'} />
    </td>
  );
}

/** The table in ghost mode — real headers, grey rows, nothing that acts on data. */
function GhostTable<T>({
  columns: ownColumns, captionKey, captionValues, captionHidden = true, className, headerRowClassName,
  scroll = true, describedBy, ghostRows = GHOST_TABLE_ROWS, thumbnail, tourAnchor,
}: DataTableProps<T>): React.JSX.Element {
  const { t } = useTranslation();
  const columns = thumbnail === undefined ? ownColumns : [thumbnailColumn(thumbnail), ...ownColumns];
  // Busy only while a read is under way (C7): a ghost over a page waiting for a choice is not.
  const reading = useReadInFlight();
  const table = (
    <table
      className={className ?? 'w-full text-sm'}
      aria-describedby={describedBy}
      aria-busy={reading}
      data-ghost-table=""
      // The tour's anchor holds in the grey state too: a step about the table still has a box to mark.
      data-tour={tourAnchor}
    >
      <caption className={captionHidden ? 'sr-only' : 'text-start text-caption text-ink-secondary'}>
        {captionKey === undefined ? t('ghost.table') : t(captionKey, captionValues ?? {})}
      </caption>
      <thead>
        <tr className={headerRowClassName}>
          {columns.map((column) => (
            <th
              key={column.id}
              scope="col"
              className={[HEAD_CELL, column.headerClassName ?? '', column.align === 'end' ? 'text-end' : 'text-start'].join(' ')}
            >
              {column.header ?? t(column.headerKey, column.headerValues ?? {})}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: ghostRows }, (_, index) => (
          <tr key={index} className="border-hairline border-b">
            {columns.map((column) => (
              <GhostCell
                key={column.id}
                shape={column.ghost ?? (column.kind === 'number' ? 'number' : 'text')}
                {...(column.align === undefined ? {} : { align: column.align })}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
  // A figure inside a column's own `header` node (a unit under a series name) is ghosted too.
  //
  // The scroller is FOCUSABLE, as the planning grid's viewport is. The real table's scroller
  // holds its sortable header buttons, so a keyboard reaches and scrolls it; the silhouette
  // holds no control at all, and a silhouette wider than its column (the exit plan book's
  // seventeen columns at 1440px) was a scrollable region no keyboard could scroll — axe's
  // `scrollable-region-focusable`, on `/exit/plans` in both themes.
  const label = captionKey === undefined ? t('ghost.table') : t(captionKey, captionValues ?? {});
  return (
    <GhostProvider>
      <div className="relative flex min-w-0 flex-col gap-2" data-layout-block="">
        {scroll
          ? <div className={`overflow-x-auto ${FOCUS_RING_INSET}`} tabIndex={0} role="region" aria-label={label}>{table}</div>
          : table}
        {/* While the read runs, the silhouette says it is loading, centred over its grey rows (ADR 9075):
            grey rows alone read as a void. It takes no click; the table says `aria-busy`. */}
        {reading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-loading-region="table">
            <span className="bg-surface-raised rounded-chip px-4 py-3"><LoadingIndicator /></span>
          </div>
        )}
      </div>
    </GhostProvider>
  );
}

// ── the CSV of a table that has no builder of its own ────────────────────────────

/**
 * The table as drawn, as an export request — the CSV every table offers when its chapter has
 * not written a builder for it (ADR 9010).
 *
 * The columns are the table's columns that carry a `value`; an action or checkbox column has
 * none and leaves nothing. Numbers leave as raw numbers, never as the display string; dates as
 * ISO; an enum as its label with its code beside it, so a filter can be rebuilt; an absence as
 * `missing`, never `0` (reqs/00 D8). The provenance says what this is and nothing more: the
 * table on screen, with every field the table cannot vouch for declared absent (ADR 9007)
 * rather than filled in.
 */
export function tableRequest<T>({ id, titleKey, columns, rows, t }: {
  readonly id: string;
  readonly titleKey: string;
  readonly columns: readonly DataColumn<T>[];
  readonly rows: readonly T[];
  readonly t: TFunction;
}): ExportRequest {
  const exported = columns.filter((column) => column.kind !== undefined && column.value !== undefined);
  const cell = (column: DataColumn<T>, row: T): ExportCell => {
    const value = column.value!(row);
    if (!isPresent(value)) return column.blankWhenAbsent === true ? { kind: 'text', value: '' } : { kind: 'missing', state: 'not-reported' };
    if (column.kind === 'number') {
      const number = Number(value);
      return Number.isFinite(number) ? { kind: 'number', raw: number } : { kind: 'text', value: String(value) };
    }
    if (column.kind === 'date') return { kind: 'date', iso: String(value) };
    if (column.kind === 'enum') {
      const code = String(value);
      return { kind: 'label', value: column.label ? column.label(code) : code, code };
    }
    return { kind: 'text', value: String(value) };
  };
  const kindOf = (column: DataColumn<T>): ExportColumn['kind'] => (
    column.kind === 'number' ? 'number' : column.kind === 'date' ? 'date' : column.kind === 'enum' ? 'label' : 'text'
  );
  const body: ExportCell[][] = rows.map((row) => exported.map((column) => cell(column, row)));
  return {
    id,
    titleKey,
    columns: exported.map((column): ExportColumn => ({
      key: column.id,
      headerKey: column.headerKey,
      kind: kindOf(column),
      // The workbook's header is the one on screen, placeholders filled (DEC-104).
      ...(column.headerValues === undefined ? {} : { headerValues: column.headerValues }),
      // What the column means, as its (i) says it (ADR 9045, 9047).
      ...(column.descriptionKey === undefined ? {} : { descriptionKey: column.descriptionKey }),
      ...(column.descriptionValues === undefined ? {} : { descriptionValues: column.descriptionValues }),
    })),
    rows: () => body,
    rowCount: body.length,
    provenance: {
      filters: [],
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: id,
        definition: t('table.export.asDrawn'),
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: exported.map((column) => column.id),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The download toolbar for a table that draws its own body with `useTableView` — the exception
 * queue, the windowed allocation list. The same CSV `DataTable` offers: the chapter's builder
 * when given, the table as drawn otherwise, over the view. Render it directly above the
 * table, as `DataTable` does (F7).
 */
export function DataTableDownload<T>({ state, id, titleKey, exportRequest, shown }: {
  readonly state: TableView<T>;
  readonly id: string;
  readonly titleKey: string;
  readonly exportRequest?: (rows: readonly T[]) => ExportRequest | Promise<ExportRequest>;
  /** How many rows the table draws; the view's length when it draws them all. */
  readonly shown?: number;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const build = exportRequest ?? ((rows: readonly T[]) => tableRequest({ id, titleKey, columns: state.columns, rows, t }));
  return (
    <RowCapNotice
      shown={shown ?? state.view.length}
      total={state.view.length}
      always
      exportRequest={async () => state.exportView(await build(state.view))}
    />
  );
}

/** A chip's words for one active filter. */
function describe<T>(column: DataColumn<T>, filter: ColumnFilter, t: TFunction): string {
  const name = t(column.headerKey, column.headerValues ?? {});
  if (filter.kind === 'contains') return t('table.filter.chip.contains', { column: name, value: filter.text.trim() });
  if (filter.kind === 'values') {
    const words = filter.values.map((value) => (
      value === NO_VALUE ? t('table.filter.noValue') : column.label ? column.label(value) : value
    ));
    return t('table.filter.chip.values', { column: name, values: words.join(', ') });
  }
  const from = filter.from.trim();
  const to = filter.to.trim();
  if (from !== '' && to !== '') return t('table.filter.chip.between', { column: name, from, to });
  if (from !== '') return t('table.filter.chip.from', { column: name, from });
  return t('table.filter.chip.to', { column: name, to });
}

// ── one column's filter control ────────────────────────────────────────────────

function FilterControl<T>({ column, rows, filter, onChange }: {
  readonly column: DataColumn<T>;
  readonly rows: readonly T[];
  readonly filter: ColumnFilter | undefined;
  readonly onChange: (filter: ColumnFilter | undefined) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const kind = column.kind ?? 'text';
  const active = isActive(filter);
  const name = t(column.headerKey, column.headerValues ?? {});

  return (
    <Popover
      triggerLabelKey="table.filter.open"
      trigger={(
        <IconButton
          variant="ghost"
          size="sm"
          icon={<ListFilter size={12} aria-hidden />}
          aria-label={t(active ? 'table.filter.openActive' : 'table.filter.open', { column: name })}
          aria-pressed={active}
          className={`size-6 p-0 ${active ? 'text-ink-primary bg-surface-sunken' : 'text-ink-muted'}`}
        />
      )}
      align={column.align === 'end' ? 'end' : 'start'}
    >
      <div className="flex min-w-[14rem] flex-col gap-2">
        <p className="text-label text-ink-secondary">{t('table.filter.title', { column: name })}</p>
        {/* A text column COLLECTS values, like an enum (client request, 2026-10-06: a filter is
            not a free-text box). It differs only in having too many to list unfiltered, so its
            picker narrows — and the narrowing box is not itself a filter: typing in it and
            collecting nothing changes nothing, which the "contains" box could not do. */}
        {kind === 'text' && <ValuesFilter column={column} rows={rows} filter={filter} onChange={onChange} narrowable />}
        {(kind === 'number' || kind === 'date') && (
          <div className="grid grid-cols-2 gap-2">
            <Input
              labelKey="table.filter.from"
              numeric={kind === 'number'}
              inputMode={kind === 'number' ? 'decimal' : undefined}
              type={kind === 'date' ? 'date' : 'text'}
              value={filter?.kind === 'range' ? filter.from : ''}
              onChange={(event) => onChange({
                kind: 'range', from: event.target.value, to: filter?.kind === 'range' ? filter.to : '',
              })}
            />
            <Input
              labelKey="table.filter.to"
              numeric={kind === 'number'}
              inputMode={kind === 'number' ? 'decimal' : undefined}
              type={kind === 'date' ? 'date' : 'text'}
              value={filter?.kind === 'range' ? filter.to : ''}
              onChange={(event) => onChange({
                kind: 'range', from: filter?.kind === 'range' ? filter.from : '', to: event.target.value,
              })}
            />
          </div>
        )}
        {kind === 'enum' && <ValuesFilter column={column} rows={rows} filter={filter} onChange={onChange} />}
        {(kind === 'number' || kind === 'date') && (
          <p className="text-caption text-ink-secondary max-w-[16rem]">{t('table.filter.absentExcluded')}</p>
        )}
        <div>
          <Button variant="ghost" size="sm" disabled={!active} onClick={() => onChange(undefined)}>
            {t('table.filter.clear')}
          </Button>
        </div>
      </div>
    </Popover>
  );
}

function ValuesFilter<T>({ column, rows, filter, onChange, narrowable = false }: {
  readonly column: DataColumn<T>;
  readonly rows: readonly T[];
  readonly filter: ColumnFilter | undefined;
  readonly onChange: (filter: ColumnFilter | undefined) => void;
  /**
   * A box that narrows the values on offer, for a column with too many to read: a text column
   * (an article code, a store key) rather than an enum, whose whole point is that its values are
   * few and nameable. It filters the LIST, never the table.
   */
  readonly narrowable?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const options = useMemo(() => {
    const seen = new Set<string>();
    let absent = false;
    for (const row of rows) {
      const value = column.value?.(row);
      if (isPresent(value)) seen.add(String(value));
      else absent = true;
    }
    const collator = new Intl.Collator(i18n.language, { numeric: true, sensitivity: 'base' });
    const label = (value: string): string => (column.label ? column.label(value) : value);
    const sorted = [...seen].sort((a, b) => collator.compare(label(a), label(b)));
    return absent ? [...sorted, NO_VALUE] : sorted;
  }, [rows, column, i18n.language]);
  // Memoised: the narrowing list depends on it, and a fresh array each render would rebuild
  // the list on every keystroke of the box above it.
  const picked = useMemo(() => (filter?.kind === 'values' ? filter.values : []), [filter]);
  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!narrowable || needle === '') return options;
    const label = (value: string): string => (column.label ? column.label(value) : value);
    // A value already picked stays on the list whatever the narrowing says, so a planner cannot
    // narrow a collected value out of sight and lose the only control that takes it off again.
    return options.filter((value) => picked.includes(value)
      || label(value).toLocaleLowerCase().includes(needle) || value.toLocaleLowerCase().includes(needle));
  }, [options, query, narrowable, column, picked]);
  const listed = shown.slice(0, VALUE_OPTION_CAP);

  return (
    <div className="flex flex-col gap-2">
      {narrowable && (
        <>
          <Input labelKey="table.filter.narrow" value={query} onChange={(event) => setQuery(event.target.value)} />
          {shown.length > listed.length && (
            <p className="text-caption text-ink-secondary">
              {t('table.filter.narrowCapped', { shown: listed.length, matching: shown.length })}
            </p>
          )}
          {shown.length === 0 && <p className="text-caption text-ink-secondary">{t('table.filter.noMatch')}</p>}
        </>
      )}
      <div className="flex max-h-[16rem] flex-col gap-1 overflow-y-auto">
        {listed.map((value) => (
          <Checkbox
            key={value}
            labelKey={value === NO_VALUE ? 'table.filter.noValue' : 'table.filter.valueOption'}
            labelValues={{ value: value === NO_VALUE ? '' : column.label ? column.label(value) : value }}
            checked={picked.includes(value)}
            onCheckedChange={(checked) => onChange({
              kind: 'values',
              values: checked ? [...picked, value] : picked.filter((entry) => entry !== value),
            })}
          />
        ))}
      </div>
    </div>
  );
}
